import { useQuery } from "@tanstack/react-query";
import { journalsQueries } from "@/entities/journal";
import { useCoordinator, useNavigationPosition } from "@/shared/lib/hooks";
import { useAppRoutes } from "@shared/lib/react-router";
import type { JournalPersonnelSectionType } from "./types";

export type JournalSectionPersonnelPageNumber = 1 | 2 | 3 | 4;

type SectionPersonnelMeta = {
  sectionType: JournalPersonnelSectionType;
  recordsTitle: string;
  getSectionId: (mainJournal?: {
    mainJournal?: {
      section1?: { sectionId?: string };
      section2?: { sectionId?: string };
      section3?: { sectionId?: string };
      section4?: { sectionId?: string };
    };
  }) => string | undefined;
  getRecordsPath: (
    journalsRoutes: ReturnType<typeof useAppRoutes>["journals"],
    params: {
      projectId: string;
      objectId: string;
      mainJournalId: string;
      titleId: string;
      mainJournalVersionId: string;
    },
    isVersion: boolean,
  ) => string;
};

const SECTION_PERSONNEL_META: Record<
  JournalSectionPersonnelPageNumber,
  SectionPersonnelMeta
> = {
  1: {
    sectionType: "sectionOne",
    recordsTitle: "Записи раздела 1",
    getSectionId: (mainJournal) => mainJournal?.mainJournal?.section1?.sectionId,
    getRecordsPath: (journalsRoutes, params, isVersion) =>
      isVersion
        ? journalsRoutes.journalVersionSection1(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
            params.mainJournalVersionId,
          )
        : journalsRoutes.section1(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
          ),
  },
  2: {
    sectionType: "sectionTwo",
    recordsTitle: "Записи раздела 2",
    getSectionId: (mainJournal) => mainJournal?.mainJournal?.section2?.sectionId,
    getRecordsPath: (journalsRoutes, params, isVersion) =>
      isVersion
        ? journalsRoutes.journalVersionSection2(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
            params.mainJournalVersionId,
          )
        : journalsRoutes.section2(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
          ),
  },
  3: {
    sectionType: "sectionThree",
    recordsTitle: "Записи раздела 3",
    getSectionId: (mainJournal) => mainJournal?.mainJournal?.section3?.sectionId,
    getRecordsPath: (journalsRoutes, params, isVersion) =>
      isVersion
        ? journalsRoutes.journalVersionSection3(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
            params.mainJournalVersionId,
          )
        : journalsRoutes.section3(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
          ),
  },
  4: {
    sectionType: "sectionFour",
    recordsTitle: "Записи раздела 4",
    getSectionId: (mainJournal) => mainJournal?.mainJournal?.section4?.sectionId,
    getRecordsPath: (journalsRoutes, params, isVersion) =>
      isVersion
        ? journalsRoutes.journalVersionSection4(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
            params.mainJournalVersionId,
          )
        : journalsRoutes.section4(
            params.projectId,
            params.objectId,
            params.mainJournalId,
            params.titleId,
          ),
  },
};

export const useJournalSectionPersonnelPageConfig = (
  sectionNumber: JournalSectionPersonnelPageNumber,
) => {
  const { mainJournalId, titleId, projectId, objectId, mainJournalVersionId } =
    useCoordinator();
  const { isInJournalVersionSection } = useNavigationPosition();
  const appRoutes = useAppRoutes();
  const meta = SECTION_PERSONNEL_META[sectionNumber];

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const routeParams = {
    projectId,
    objectId,
    mainJournalId,
    titleId,
    mainJournalVersionId,
  };

  return {
    sectionNumber,
    sectionType: meta.sectionType,
    sectionId: meta.getSectionId(mainJournalData),
    recordsTitle: meta.recordsTitle,
    recordsPath: meta.getRecordsPath(
      appRoutes.journals,
      routeParams,
      isInJournalVersionSection,
    ),
  };
};
